<template>
    <h4 class="text-lg text-gray-900 dark:text-white font-semibold">Trade Ads</h4>
    <p class="font-medium dark:text-white">Browse thousands of trade ads from other active Roblox traders or create your own.</p>
    <p class="font-medium dark:text-white">Trade ads help connect traders so you get more trades completed!</p>
    <div class="my-4">
        <NuxtLink to="/ps-99/ads/create">
            <ShadButton>
                Create Ad
            </ShadButton>
        </NuxtLink>
    </div>
    <div class="mt-6 border-l-4 border-yellow-400 bg-yellow-50 p-4">
        <div class="flex">
            <div class="flex-shrink-0">
                <ExclamationTriangleIcon class="h-5 w-5 text-yellow-400" aria-hidden="true" />
            </div>
            <div class="ml-3">
                <p class="text-sm font-medium text-yellow-700">
                    Values have been predetermined and are subject to fluctuate.
                </p>
            </div>
        </div>
    </div>
    <div class="mt-6 rounded-md bg-[#f5f5f5] dark:bg-[#202226] py-6 px-6">
        <div class="grid xl:flex gap-y-6 gap-x-8">
            <div class="flex-1 grid gap-2.5">
                <ShadLabel class="font-semibold">Filter Offers</ShadLabel>
                <Filter v-model:name="filters.offer.name" v-model:category="filters.offer.category" v-model:rarity="filters.offer.rarity" v-model:demand="filters.offer.demand" v-model:minValue="filters.offer.minValue" v-model:maxValue="filters.offer.maxValue" v-model:addon="filters.offer.addon" />
            </div>
            <div class="flex-1 grid gap-2.5">
                <ShadLabel class="font-semibold">Filter Requests</ShadLabel>
                <Filter v-model:name="filters.request.name" v-model:category="filters.request.category" v-model:rarity="filters.request.rarity" v-model:demand="filters.request.demand" v-model:minValue="filters.request.minValue" v-model:maxValue="filters.request.maxValue" v-model:addon="filters.request.addon" />
            </div>
        </div>
    </div>
    <div class="mt-4">
        <Pagination v-model="filters.page" :total-items="ads?.totalCount" :items-per-page="filters.limit" />
        <ClientOnly>
            <div class="grid gap-y-4" :class="{'my-4': ads?.totalCount! > filters.limit}" v-if="ads?.data.length! > 0">
                <ShadTooltipProvider>
                    <div v-for="ad in ads?.data" class="grid gap-y-6 rounded-md bg-[#f5f5f5] dark:bg-[#202226] py-8 px-8">
                        <div class="items-center justify-between" :class="{'sm:flex': ad.discordTag, 'flex': !ad.discordTag}">
                            <div class="flex items-center gap-x-4">
                                <NuxtImg :src="`https://rbx.how/user/${ad.user.robloxId}`" class="h-8" />
                                <div>
                                    <span class="dark:text-white font-semibold">{{ ad.user.robloxName }}</span>
                                    <p class="dark:text-white text-xs font-medium">{{ dayjs(ad.createdAt).fromNow() }}</p>
                                </div>
                            </div>
                            <div class="flex justify-between items-center gap-x-5" :class="{'mt-4 sm:mt-0': ad.discordTag}">
                                <div v-if="ad.discordTag">
                                    <p class="text-sm font-semibold text-[#7289da]">
                                        <i class="fa-brands fa-discord mr-1"></i> {{ ad.discordTag }}
                                    </p>
                                </div>
                                <NuxtLink :to="`/ps-99/ad/${ad.id}`">
                                    <Button>Details</Button>
                                </NuxtLink>
                            </div>
                        </div>
                        <div class="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-y-8 gap-x-10 text-center">
                            <div class="flex flex-col">
                                <p class="dark:text-white font-semibold">Offer</p>
                                <div class="grid grid auto-fill-[140px] gap-3 my-6">
                                    <ShadTooltip v-for="(offer, index) in ad.offer" :key="index">
                                        <ShadTooltipTrigger as-child>
                                            <template v-if="offer.type == 'item'">
                                                <NuxtLink :to="`/ps-99/item/${isFxData(offer.data) && offer.data.id}`">
                                                    <div class="group h-36 p-6 relative rounded-md bg-[#efefef] dark:bg-[#2c2e32] flex flex-col items-center justify-center">
                                                        <NuxtImg :src="offer.data.icon" class="w-full h-full object-contain" />
                                                    </div>
                                                </NuxtLink>
                                            </template>
                                            <template v-else>
                                                <div class="group h-36 p-6 relative rounded-md bg-[#efefef] dark:bg-[#2c2e32] flex flex-col items-center justify-center">
                                                    <NuxtImg :src="offer.data.icon" class="w-full h-full object-contain" />
                                                </div>
                                            </template>
                                        </ShadTooltipTrigger>
                                        <ShadTooltipContent>
                                            <div class="text-center" v-if="offer.type == 'item'">
                                                <p>{{ offer.data.name }}</p>
                                                <p>Value: {{ isFxData(offer.data) && offer.data.valueApplicable ? formatNumber(offer.data.value, 0, true) : 'N/A' }}</p>
                                            </div>
                                            <template v-else>
                                                {{ offer.data.name }}
                                            </template>
                                        </ShadTooltipContent>
                                    </ShadTooltip>
                                </div>
                                <p class="dark:text-white font-semibold mt-auto">Value <span class="text-primary">{{ formatNumber(getTotalOfferValue(ad.offer), 0, true) }}</span></p>
                            </div>
                            <div class="flex flex-col">
                                <p class="dark:text-white font-semibold">Request</p>
                                <div class="grid grid auto-fill-[140px] gap-3 my-6">
                                    <ShadTooltip v-for="(request, index) in ad.request" :key="index">
                                        <ShadTooltipTrigger as-child>
                                            <template v-if="request.type == 'item'">
                                                <NuxtLink :to="`/ps-99/item/${isFxData(request.data) && request.data.id}`">
                                                    <div class="group h-36 p-6 relative rounded-md bg-[#efefef] dark:bg-[#2c2e32] flex flex-col items-center justify-center">
                                                        <NuxtImg :src="request.data.icon" class="w-full h-full object-contain" />
                                                    </div>
                                                </NuxtLink>
                                            </template>
                                            <template v-else>
                                                <div class="group h-36 p-6 relative rounded-md bg-[#efefef] dark:bg-[#2c2e32] flex flex-col items-center justify-center">
                                                    <NuxtImg :src="request.data.icon" class="w-full h-full object-contain" />
                                                </div>
                                            </template>
                                        </ShadTooltipTrigger>
                                        <ShadTooltipContent>
                                            <div class="text-center" v-if="request.type == 'item'">
                                                <p>{{ request.data.name }}</p>
                                                <p>Value: {{ isFxData(request.data) && request.data.valueApplicable ? formatNumber(request.data.value, 0, true) : 'N/A' }}</p>
                                            </div>
                                            <template v-else>
                                                {{ request.data.name }}
                                            </template>
                                        </ShadTooltipContent>
                                    </ShadTooltip>
                                </div>
                                <p class="dark:text-white font-semibold mt-auto">Value <span class="text-primary">{{ formatNumber(getTotalRequestValue(ad.request), 0, true) }}</span></p>
                            </div>
                        </div>
                    </div>
                </ShadTooltipProvider>
            </div>
        </ClientOnly>
        <Pagination v-model="filters.page" :total-items="ads?.totalCount" :items-per-page="filters.limit" />
    </div>
</template>

<script setup lang="ts">
import { ExclamationTriangleIcon } from '@heroicons/vue/20/solid'
import Filter from './components/Filter.vue'
import updateLocale from 'dayjs/plugin/updateLocale.js'

const dayjs = useDayjs()
dayjs.extend(updateLocale)

dayjs.updateLocale('en', {
    relativeTime: {
        future: "in %s",
        past: "%s ago",
        s: function (number: number) {
            return number === 0 ? "a few seconds" : `${number} seconds`
        },
        m: "a minute",
        mm: "%d minutes",
        h: "an hour",
        hh: "%d hours",
        d: "a day",
        dd: "%d days",
        M: "a month",
        MM: "%d months",
        y: "a year",
        yy: "%d years"
    },
})

interface AdData {
    id: number;
    user: User;
    offer: OfferItem[];
    request: RequestItem[];
    discordTag: string;
    createdAt: string;
}

interface User {
    robloxId: number;
    robloxName: string;
    avatar: string;
}

interface OfferItem {
    type: 'item' | 'addon';
    data: FxData | AddonData;
}

interface RequestItem {
    type: 'item' | 'addon';
    data: FxData | AddonData;
}

interface FxData {
    id: number;
    name: string;
    icon: string;
    value: number;
    valueApplicable: boolean;
}

interface AddonData {
    name: string;
    icon: string;
}

interface ApiResponse<T> {
    data: T[];
    currentPage: number;
    isFirstPage: boolean;
    isLastPage: boolean;
    previousPage: number | null;
    nextPage: number | null;
    pageCount: number;
    totalCount: number;
}

const filters = ref<{
    offer: {
        name: string;
        category: string[];
        rarity: string[];
        demand: string[];
        minValue: string | undefined;
        maxValue: string | undefined;
        addon: string[];
    };
    request: {
        name: string;
        category: string[];
        rarity: string[];
        demand: string[];
        minValue: string | undefined;
        maxValue: string | undefined;
        addon: string[];
    };
    page: number;
    limit: number;
}>({
    offer: {
        name: '',
        category: [] as string[],
        rarity: [] as string[],
        demand: [] as string[],
        minValue: undefined,
        maxValue: undefined,
        addon: [] as string[]
    },
    request: {
        name: '',
        category: [] as string[],
        rarity: [] as string[],
        demand: [] as string[],
        minValue: undefined,
        maxValue: undefined,
        addon: [] as string[]
    },
    page: 1,
    limit: 15
})

function isFxData(data: FxData | AddonData): data is FxData {
    return (data as FxData).value !== undefined; // Check for 'value' instead of 'valueApplicable'
}

const { data: ads } = await useFetch<ApiResponse<AdData>>('/api/ps-99/ads', {
    query: {
        offer: {
            name: computed(() => filters.value.offer.name !== '' ? filters.value.offer.name : undefined),
            category: computed(() => filters.value.offer.category),
            rarity: computed(() => filters.value.offer.rarity),
            demand: computed(() => filters.value.offer.demand),
            minValue: computed(() =>  filters.value.offer.minValue !== '' ? filters.value.offer.minValue : undefined),
            maxValue: computed(() => filters.value.offer.maxValue !== '' ? filters.value.offer.maxValue : undefined),
            addon: computed(() => filters.value.offer.addon)
        },
        request: {
            name: computed(() => filters.value.request.name !== '' ? filters.value.request.name : undefined),
            category: computed(() => filters.value.request.category),
            rarity: computed(() => filters.value.request.rarity),
            demand: computed(() => filters.value.request.demand),
            minValue: computed(() =>  filters.value.request.minValue !== '' ? filters.value.request.minValue : undefined),
            maxValue: computed(() => filters.value.request.maxValue !== '' ? filters.value.request.maxValue : undefined),
            addon: computed(() => filters.value.request.addon)
        },
        page: computed(() => filters.value.page),
        limit: filters.value.limit
    }
})

function getTotalOfferValue(offers: OfferItem[]): number {
    return offers.reduce((sum, offerItem) => {
        if (offerItem.type === 'item' && isFxData(offerItem.data) && offerItem.data.valueApplicable) {
            return sum + (offerItem.data?.value || 0);
        }
        return sum;
    }, 0);
}

function getTotalRequestValue(requests: RequestItem[]): number {
    return requests.reduce((sum, requestItem) => {
        if (requestItem.type === 'item' && isFxData(requestItem.data) && requestItem.data.valueApplicable) {
            return sum + (requestItem.data?.value || 0);
        }
        return sum;
    }, 0);
}

watch(() => [filters.value.offer, filters.value.request], () => {
    filters.value.page = 1
}, {
    deep: true
})

// Automatically update the relative times every 10 seconds
let intervalId: number | undefined;

onMounted(() => {
    intervalId = setInterval(() => {
        // Force an update by changing a reactive variable
        ads.value = { ...ads.value! };  // This triggers a re-render for reactive data like dayjs timestamps
    }, 10000) as unknown as number; // Update every 10 seconds
});

onBeforeUnmount(() => {
    if (intervalId) clearInterval(intervalId); // Clear the interval when the component unmounts
});

useSeoMeta({
    title: 'Trade Ads'
})
</script>